import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";
import MeasuredBy from "../molecules/MeasuredBy";

import {
  activeStylesContinue,
  welcomeScreenUI,
  titleStyles as titleStylesBase,
  subtitlesStyles,
  resultScreenUI,
} from "../../configs";
import { capitalizeFirstLetter, postClearMid, useIsMobile } from "../../utils";
import { isFootwear } from "../../utils/categoryParams";
import { findBrandByDomain } from "../../configs/configLoader";
import { ArrowForward as ChevronRight } from "@mui/icons-material";
import { hoverStylesContinue } from "../../configs/stylesLoader";

import "./index.css";

interface IPropsResultError {
  step: {
    number: number;
  } | null;
  nextStep: () => void;
  outOfRange: boolean;
}

const ResultError: FC<IPropsResultError> = ({ step, nextStep, outOfRange }) => {
  const isMobile = useIsMobile();

  const error_description = `error.description${
    outOfRange ? "_outOfRange" : ""
  }`;

  const isLargeHeightRelative = useMediaQuery({
    maxHeight: !isMobile ? 830 : 778,
    minWidth: 767,
  });


  const { t } = useTranslation("components/results/error");

  const [hovered, setHovered] = useState(false);

  const brandDefined = findBrandByDomain();

  const isPeserico = brandDefined?.name === "Peserico";
  const isVB = brandDefined?.name === "Victoria Beckham";
  const isIntersport = brandDefined?.name === "Intersport";
  const isBlackstore = brandDefined?.name === "Blackstore";
  const isLoom = brandDefined?.name === "Loom";
  const isGivenchy = brandDefined?.name === "Givenchy";
  const isTDF = brandDefined?.name === "TDF";
  const isPXP = brandDefined?.name === "Project X Paris";

  const isCustomInterface = isIntersport || isBlackstore;

  const isGivenchyDesktop = isGivenchy && !isMobile;

  const titleTextTransform = welcomeScreenUI.input_fields.title
    .textTransform as React.CSSProperties["textTransform"];

  const titlesStyles = {
    fontWeight: "400",
    textAlign: welcomeScreenUI.input_fields.title
      .textAlign as React.CSSProperties["textAlign"],
    textTransform:
      titleTextTransform === "capitalize" ? "none" : titleTextTransform,
    color: titleStylesBase.color as React.CSSProperties["color"],
    fontSize: "14px",
    justifyContent: welcomeScreenUI.input_fields.title
      .textAlign as React.CSSProperties["justifyContent"],
    fontStyle: (welcomeScreenUI.input_fields.title as any)
      .fontStyle as React.CSSProperties["fontStyle"],
    fontFamily: (welcomeScreenUI.input_fields.title as any)
      .fontFamily as React.CSSProperties["fontFamily"],
  };

  useEffect(() => {
    if (outOfRange) postClearMid();
  }, [outOfRange]);

  const CTAStyles: React.CSSProperties = {
    ...activeStylesContinue,
    ...(hovered ? hoverStylesContinue : {}),
    textTransform: isGivenchy
      ? "uppercase"
      : ("none" as React.CSSProperties["textTransform"]),
    whiteSpace: "nowrap",
    padding: isTDF ? "0" : isPeserico ? "12px 16px" : isVB ? "7px" : "16px",
    height: isTDF ? "28px" : isVB ? "30px" : "auto",
    maxHeight: isTDF ? "28px" : isGivenchy ? "40px" : "fit-content",
    marginBottom: isMobile ? "20px" : "0px",
    position: isGivenchyDesktop ? "fixed" : "relative",
    bottom: isGivenchyDesktop ? "150px" : 0,
    width: isGivenchyDesktop ? "calc(100% - 160px)" : "",
    backgroundColor: isGivenchy
      ? "transparent"
      : activeStylesContinue.backgroundColor,
    borderColor: isGivenchy ? "transparent" : activeStylesContinue.borderColor,
    color: isGivenchy ? "#000000" : activeStylesContinue.color,
  };

  const storeLocatorCTAStyles: React.CSSProperties = {
    ...activeStylesContinue,
    ...(isIntersport && hovered ? hoverStylesContinue : {}),
    textTransform: isGivenchy
      ? "uppercase"
      : ("none" as React.CSSProperties["textTransform"]),
    whiteSpace: "nowrap",
    padding: isTDF ? "0" : isPeserico ? "12px 16px" : isVB ? "7px" : "16px",
    height: isTDF ? "28px" : isVB ? "30px" : "auto",
    maxHeight: isTDF ? "28px" : isGivenchy ? "40px" : "fit-content",
    marginBottom: isMobile ? "20px" : "0px",
    position: isGivenchyDesktop ? "fixed" : "relative",
    bottom: isGivenchyDesktop ? "200px" : 0,
    width: isGivenchyDesktop ? "calc(100% - 160px)" : "",
  };

  const arrowContainerStyle: React.CSSProperties = {
    display: "inline-block",
    width: hovered ? 16 : 0,
    overflow: "hidden",
    verticalAlign: "middle",
    transition: "width 160ms ease",
  };

  const iconStyle: React.CSSProperties = {
    width: 16,
    height: 16,
    opacity: hovered ? 1 : 0,
    transform: hovered ? "translateX(0)" : "translateX(-6px)",
    transition: "opacity 160ms ease, transform 160ms ease",
    display: "inline-block",
    verticalAlign: "middle",
    pointerEvents: "none",
  };

  const hideErrorMessage = outOfRange && isLoom;

  const isCapitalizedSubtitle =
    subtitlesStyles.textTransform === "capitalize" && !isGivenchy && !isPXP;

  return (
    <div
      className="result-error"
      style={{
        marginTop: isFootwear
          ? "30px"
          : isLargeHeightRelative
            ? "30px"
            : isMobile
              ? 0
              : "40px",
      }}
    >
      {!hideErrorMessage ? (
        <span
          className="description"
          style={{
            ...titlesStyles,
            marginTop:
              !isMobile && brandDefined?.name === "SRP"
                ? "60px"
                : isLargeHeightRelative
                  ? 0
                  : isMobile
                    ? 0
                    : "25px",
            marginBottom: isFootwear && !outOfRange ? "30px" : "40px",
            textTransform:
              isGivenchy || isPXP
                ? "none"
                : ((subtitlesStyles.textTransform === "capitalize"
                    ? "none"
                    : subtitlesStyles.textTransform) as React.CSSProperties["textTransform"]),
            letterSpacing: (resultScreenUI.Description as any)?.letterSpacing,
          }}
          dangerouslySetInnerHTML={{
            __html:
              isFootwear && !outOfRange
                ? t("shoeMessage")
                : isFootwear && outOfRange && isCustomInterface
                  ? t("shoeMessage_outOfRange")
                  : isCapitalizedSubtitle
                    ? capitalizeFirstLetter(t(error_description))
                    : t(error_description),
          }}
        />
      ) : null}

      {isGivenchy ? (
        <button
          tabIndex={0}
          type="button"
          className={`continue-button`}
          onClick={() => {
            const scanUrl = t("storeLocatorLink");

            if (!!scanUrl) {
              window.open(scanUrl, "_blank", "noopener,noreferrer");
            }
          }}
          style={storeLocatorCTAStyles}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0,
              letterSpacing: (resultScreenUI.Description as any)?.letterSpacing,
            }}
          >
            {t("storeLocator")}
          </div>
        </button>
      ) : null}

      <button
        tabIndex={0}
        type="button"
        className={`continue-button`}
        onMouseEnter={(e) => {
          if (isMobile) return;
          if (isGivenchy) return;
          setHovered(true);
          Object.assign(e.currentTarget.style, hoverStylesContinue);
        }}
        onMouseLeave={(e) => {
          if (isGivenchy) return;

          setHovered(false);
          Object.assign(e.currentTarget.style, CTAStyles);
        }}
        onClick={() => nextStep()}
        style={CTAStyles}
      >
        <div style={{ display: "inline-flex", alignItems: "center", gap: 0 }}>
          {activeStylesContinue.textTransform === "capitalize"
            ? capitalizeFirstLetter(t("skip"))
            : t("skip")}

          {isIntersport && (
            <span style={arrowContainerStyle} aria-hidden>
              <ChevronRight style={iconStyle} />
            </span>
          )}
        </div>
      </button>

      <MeasuredBy step={step} />
    </div>
  );
};

export default ResultError;
